/* eslint-disable react/no-invalid-html-attribute */
/**
 * アプリケーション共通レイアウトコンポーネント
 * 全ページに共通するヘッダー・フッター・ローディング・アラートダイアログを提供する。
 * ページ遷移履歴の管理、ページタイトルの自動設定、
 * チャットボット・ Google Tag Manager・バナー表示なども担う。
 */
import historyContext from '@/hooks/historyContext';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { ReactNode, useEffect, useState } from 'react';
import { PagePathMobile } from '@/mobile/utils/constants';
import useServiceType from '@/hooks/useServiceType';
import { Constants } from '@/utils/constants';
import url from '../utils/config';
import AlertDialogGlobal from './molecules/AlertDialogGlobal';
import AlertDialog from './organisms/AlertDialog';
import ChatBot from './templates/ChatBot';
import Footer from './templates/Footer';
import { GoogleTagManager } from './templates/GoogleTagManager';
import Header from './templates/Header';
import LoadingIcon from './templates/LoadingIcon';
import LoginMessageBanner from './organisms/LoginMessageBanner';

/** レイアウトコンポーネントのProps */
type Props = {
  children: ReactNode;
};

/**
 * URLパスとページタイトルのマッピング
 * ブラウザのタブタイトル表示やSEO用の<title>タグに使用される。
 */
const titleMap: { [key: string]: string } = {
  '/login': 'マイページログイン',
  '/login/failure': 'ログインエラー',
  '/login_history': 'ログイン履歴',
  '/service': 'サービス選択',
  '/password_reset': 'ユーザーIDの確認とパスワードの再設定',
  '/password_reset/onetime_password': 'ワンタイムパスワード入力',
  '/password_reset/resetting': 'パスワード再設定',
  '/password_reset/complete': 'パスワード設定完了',
  '/top': '料金・お知らせ',
  '/top/billing': 'ご利用料金明細',
  '/top/delivery': 'ご利用機器情報',
  '/top/delivery/change_collection': '返却予定日変更',
  '/top/delivery/change_collection/confirm': '返却予定日変更確認',
  '/top/delivery/change_collection/complete': '返却予定日変更完了',
  '/top/delivery/change_delivery': '配送希望日変更',
  '/top/delivery/change_delivery/confirm': '配送希望日変更確認',
  '/top/delivery/change_delivery/complete': '配送希望日変更完了',
  '/top/history': 'ご利用料金・お支払い状況一覧',
  '/top/invoice': 'ご利用料金明細',
  '/top/voice': '通話明細',
  '/contract': 'ご契約情報',
  '/contract/construction': 'NURO 光 回線引込み工事承諾',
  '/contract/contractor': '契約者情報変更',
  '/contract/contractor/confirm': '契約者情報変更確認',
  '/contract/contractor/complete': '契約者情報変更完了',
  '/contract/contractor/billing_address': '請求書送付先住所変更',
  '/contract/contractor/billing_address/confirm': '請求書送付先住所確認',
  '/contract/contractor/billing_address/complete': '請求書送付先住所完了',
  '/contract/entry_cancel': 'NURO 光 回線申込キャンセル',
  '/contract/entry_cancel/enter': '回線申込キャンセル注意事項',
  '/contract/entry_cancel/form': '回線申込キャンセル内容の入力',
  '/contract/entry_cancel/confirm': '回線申込キャンセル確認',
  '/entry_cancel': '回線申込キャンセル完了',
  '/contract/g10upgrade': '10Gアップグレードお申込み',
  '/contract/g10upgrade/areacheck': '10Gアップグレード提供エリア判定',
  '/contract/g10upgrade/result': '10Gアップグレード申込条件判定',
  '/contract/g10upgrade/applicationplan': '',
  '/contract/g10upgrade/agreement': '',
  '/contract/g10upgrade/completed': '10Gアップグレード申込完了',
  '/contract/g10upgrade/completederror': '10Gアップグレード申込エラー',
  '/contract/id_password': 'ID・パスワード変更',
  '/contract/id_password/confirm': 'ID・パスワード変更確認',
  '/contract/id_password/complete': 'ID・パスワード変更完了',
  '/contract/id_password/error': 'ID・パスワード変更エラー',
  '/contract/mail': 'メールアドレス登録・変更',
  '/contract/mail/confirm': 'メールアドレス登録・変更確認',
  '/contract/mail/complete': 'メールアドレス登録・変更完了',
  '/contract/mail/error': 'メールアドレス登録・変更エラー',
  '/contract/move_token': 'お引越しコード発行利用規約同意',
  '/contract/move_token/create': 'お引越しコード発行',
  '/contract/move_token/error': 'お引越しコード発行エラー',
  '/contract/payment': 'お支払い情報の登録・変更',
  '/contract/payment/credit_card': 'クレジットカード情報の登録・変更',
  '/contract/payment/credit_card/confirm': 'クレジットカード情報の登録・変更確認',
  '/contract/payment/credit_card/complete': 'クレジットカード情報の登録・変更完了',
  '/contract/payment/docomo': 'NTTドコモ合算請求情報の登録・変更',
  '/contract/payment/docomo/complete': 'NTTドコモ合算請求情報の登録・変更完了',
  '/contract/payment/softbank': 'ソフトバンクまとめて支払い情報の確認・変更',
  '/contract/payment/softbank/confirm': 'ソフトバンクまとめて支払い情報の確認・変更確認',
  '/contract/payment/softbank/complete': 'ソフトバンクまとめて支払い情報の確認・変更完了',
  '/contract/payment/kddi': 'auかんたん決済情報の確認・変更',
  '/contract/payment/kddi/confirm': 'auかんたん決済情報の確認・変更確認',
  '/contract/payment/kddi/complete': 'auかんたん決済情報の確認・変更完了',
  '/contract/payment/account_transfer': '口座振替情報の登録・変更',
  '/contract/poreport': 'Hands-up 活動報告書',
  '/contract/promotion': 'プロモーション情報配信設定',
  '/contract/promotion/confirm': 'プロモーション情報配信設定確認',
  '/contract/promotion/complete': 'プロモーション情報配信設定完了',
  '/contract/quit_cancel': '解約取り消し',
  '/contract/quit_cancel/complete': '解約取り消し完了',
  '/contract/quit_contract': '解約',
  '/contract/quit_contract/step1_enter': '解約方法入力',
  '/contract/quit_contract/step2_confirm': '解約費用確認',
  '/contract/quit_contract/step3_confirm': '解約内容確認',
  '/contract/quit_contract/complete': '解約完了',
  '/contract/sony_account': 'ソニーアカウント',
  '/contract/sony_account/withdraw': 'ソニーアカウント退会',
  '/option': 'ご契約オプション',
  '/option/entry': 'オプション申込',
  '/option/entry/confirm': 'オプション申込確認',
  '/option/entry/complete': 'オプション申込完了',
  '/option/quit': 'オプション解約',
  '/option/quit/confirm': 'オプション解約確認',
  '/option/quit/complete': 'オプション解約完了',
  '/option/entry_cancel': 'オプション申込キャンセル',
  '/option/entry_cancel/complete': 'オプション申込キャンセル完了',
  '/option/bravia_recycle': 'ブラビア 月額オプション リサイクル申込',
  '/option/bravia_recycle/complete': 'ブラビア 月額オプション リサイクル申込完了',
  '/option/nuro_denki/plan_change': 'NUROでんきプラン変更',
  '/option/nuro_denki/plan_change/confirm': 'NUROでんきプラン変更確認',
  '/option/nuro_denki/plan_change/complete': 'NUROでんきプラン変更完了',
  '/option/rvoip': 'NURO 光 でんわ One Type付加サービス変更',
  '/option/rvoip/change': 'NURO 光 でんわ One Type付加サービス変更',
  '/option/rvoip/change/confirm': 'NURO 光 でんわ One Type付加サービス変更確認',
  '/option/secom/entry': 'セコム駆けつけ申込',
  '/option/secom/entry/input': 'セコム駆けつけ申込入力',
  '/option/secom/entry/confirm': 'セコム駆けつけ申込確認',
  '/option/secom/quit': 'セコム駆けつけ解約',
  '/option/secom/quit/complete': 'セコム駆けつけ解約完了',
  '/option/unext': 'U-NEXT for NURO',
  '/option/unext/change': 'U-NEXT for NURO変更',
  '/option/unext/change/complete': 'U-NEXT for NURO変更完了',
  '/option/unext/quit': 'U-NEXT for NURO解約',
  '/option/unext/quit/complete': 'U-NEXT for NURO解約完了',
  '/option/voip': 'NURO 光 でんわ申込',
  '/option/voip/confirm': 'NURO 光 でんわ申込確認',
  '/option/voip/change': 'NURO 光 でんわ付加サービス変更',
  '/option/voip/change/confirm': 'NURO 光 でんわ付加サービス変更確認',
  '/option/voip/quit': 'NURO 光 でんわ解約',
  '/option/voip/quit/confirm': 'NURO 光 でんわ解約確認',
  '/option/voip/wc24': 'ホワイトコール24申込書請求',
  '/option/voip/web_detail': 'Web明細ログインID/パスワード再発行',
  '/campaign': '特典・キャンペーン',
  '/campaign/detail': '特典・キャンペーン詳細',
  '/campaign/cashback_register': 'キャッシュバック口座登録',
  '/campaign/cashback_register/wcos': 'キャッシュバック口座登録',
  '/campaign/present': 'プレゼントキャンペーン応募',
  '/campaign/present/complete': 'プレゼントキャンペーン応募完了',
  '/notification_history': '通知履歴',
  '/operation': 'ユーザ検索',
  '/operation/search': 'ユーザ検索',
  '/mobile/top': '料金・お知らせ',
  '/mobile/top/billing': 'ご利用料金明細',
  '/mobile/top/calling': '通話料・SMS通信料明細',
  '/mobile/top/data_advance': 'データ前借り',
  '/mobile/top/data_advance/confirm': 'データ前借り確認',
  '/mobile/top/data_advance/complete': 'データ前借り完了',
  '/mobile/top/charge': 'チャージ',
  '/mobile/top/charge/confirm': 'チャージ確認',
  '/mobile/top/charge/complete': 'チャージ完了',
  '/mobile/top/charge_neo_trial': 'NEOトライアル',
  '/mobile/top/charge_neo_trial/confirm': 'NEOトライアル確認',
  '/mobile/top/charge_neo_trial/complete': 'NEOトライアル完了',
  '/mobile/top/packet_gift': 'パケットギフト',
  '/mobile/top/packet_gift/receipt': 'パケットギフト受取',
  '/mobile/top/packet_gift/receipt/complete': 'パケットギフト受取完了',
  '/mobile/top/packet_gift/entry': 'パケットギフト申込',
  '/mobile/top/packet_gift/entry/confirm': 'パケットギフト申込確認',
  '/mobile/top/packet_gift/entry/complete': 'パケットギフト申込完了',
  '/mobile/top/giga_plus': 'データ容量受取',
  '/mobile/top/giga_plus/complete': 'データ容量受取完了',
  '/mobile/top/invoice': 'ご利用料金明細',
  '/mobile/top/mnp': 'MNP開通予約',
  '/mobile/top/mnp/complete': 'MNP開通予約完了',
  '/mobile/top/reimbursement': '返還請求明細',
  '/mobile/top/usage_status': 'ご利用状況',
  '/mobile/contract': 'ご契約情報',
  '/mobile/contract/contractor': '契約者情報変更',
  '/mobile/contract/contractor/confirm': '契約者情報変更確認',
  '/mobile/contract/contractor/complete': '契約者情報変更完了',
  '/mobile/contract/mail': 'メールアドレス登録・変更',
  '/mobile/contract/mail/confirm': 'メールアドレス登録・変更確認',
  '/mobile/contract/mail/complete': 'メールアドレス登録・変更完了',
  '/mobile/contract/mail/error': 'メールアドレス登録・変更エラー',
  '/mobile/contract/password': 'パスワード変更',
  '/mobile/contract/password/confirm': 'パスワード変更確認',
  '/mobile/contract/password/complete': 'パスワード変更完了',
  '/mobile/contract/password/error': 'パスワード変更エラー',
  '/mobile/contract/payment': 'お支払い情報の登録・変更',
  '/mobile/contract/payment/confirm': 'お支払い情報の登録・変更確認',
  '/mobile/contract/payment/complete': 'お支払い情報の登録・変更完了',
  '/mobile/contract/promotion': 'プロモーション情報配信設定',
  '/mobile/contract/promotion/confirm': 'プロモーション情報配信設定確認',
  '/mobile/contract/promotion/complete': 'プロモーション情報配信設定完了',
  '/mobile/contract/plan_entry': 'プランの変更',
  '/mobile/contract/plan_entry/input': 'プランの変更入力',
  '/mobile/contract/plan_entry/confirm': 'プランの変更確認',
  '/mobile/contract/plan_entry/complete': 'プランの変更完了',
  '/mobile/contract/detail': 'ご契約内容',
  '/mobile/contract/sim_stop': '利用停止',
  '/mobile/contract/sim_stop/confirm': '利用停止確認',
  '/mobile/contract/sim_stop/complete': '利用停止完了',
  '/mobile/contract/plan_cancel': 'プラン変更予約キャンセル',
  '/mobile/contract/plan_cancel/confirm': 'プラン変更予約キャンセル確認',
  '/mobile/contract/plan_cancel/complete': 'プラン変更予約キャンセル完了',
  '/mobile/contract/quit': '解約',
  '/mobile/contract/quit/confirm': '月末解約確認',
  '/mobile/contract/quit/confirmMnp': 'MNP転出解約確認',
  '/mobile/contract/quit/confirmRentalWifi': 'レンタルWi-Fi解約確認',
  '/mobile/contract/quit/onestop': 'MNPワンストップ解約',
  '/mobile/contract/quit/onestop/confirm': 'MNPワンストップ解約確認',
  '/mobile/contract/quit/onestop/error': 'MNPワンストップ解約エラー',
  '/mobile/contract/quit/complete': '解約完了',
  '/mobile/contract/sim_restart': '利用再開',
  '/mobile/contract/sim_restart/confirm': '利用再開確認',
  '/mobile/contract/sim_restart/complete': '利用再開完了',
  '/mobile/contract/esim_profile': 'eSIMプロファイルダウンロード',
  '/mobile/contract/esim': 'eSIM交換（再発行）',
  '/mobile/contract/esim/confirm': 'eSIM交換（再発行）確認',
  '/mobile/contract/esim/complete': 'eSIM交換（再発行）完了',
  '/mobile/contract/device': '端末購入',
  '/mobile/contract/device/delivery_setting': '端末購入入力',
  '/mobile/contract/device/confirm': '端末購入確認',
  '/mobile/contract/device/complete': '端末購入完了',
  '/mobile/contract/traffic_control': '通信最適化',
  '/mobile/contract/traffic_control/complete': '通信最適化完了',
  '/mobile/contract/procedure': '手続き履歴',
  '/mobile/option': 'オプション一覧',
  '/mobile/option/phone_protection_imei': '端末補償 IMEI変更',
  '/mobile/option/phone_protection_imei/confirm': '端末補償 IMEI変更確認',
  '/mobile/option/phone_protection_imei/complete': '端末補償 IMEI変更完了',
  '/mobile/option/phone_protection_quit': '端末補償解約',
  '/mobile/option/phone_protection_quit/complete': '端末補償解約完了',
  '/mobile/option/network_fifth_generation_entry': '5Gオプション申込',
  '/mobile/option/network_fifth_generation_entry/complete': '5Gオプション申込確認',
  '/mobile/option/network_fifth_generation_entry/confirm': '5Gオプション申込完了',
  '/mobile/option/network_fifth_generation_quit': '5Gオプション解約',
  '/mobile/option/network_fifth_generation_quit/complete': '5Gオプション解約完了',
  '/mobile/option/virus_buster_mobile_entry': 'ウイルスバスター モバイル 月額版申込',
  '/mobile/option/virus_buster_mobile_entry/confirm': 'ウイルスバスター モバイル 月額版申込確認',
  '/mobile/option/virus_buster_mobile_entry/complete': 'ウイルスバスター モバイル 月額版申込完了',
  '/mobile/option/virus_buster_mobile_quit': 'ウイルスバスター モバイル 月額版解約',
  '/mobile/option/virus_buster_mobile_quit/complete': 'ウイルスバスター モバイル 月額版解約完了',
  '/mobile/option/value_data_free_plan_entry': 'バリューデータフリー申込',
  '/mobile/option/value_data_free_plan_entry/confirm': 'バリューデータフリー申込確認',
  '/mobile/option/value_data_free_plan_entry/complete': 'バリューデータフリー申込完了',
  '/mobile/option/value_data_free_plan_quit': 'バリューデータフリー解約',
  '/mobile/option/value_data_free_plan_quit/complete': 'バリューデータフリー解約完了',
  '/mobile/option/purchase_device_entry': '端末買取サービス',
  '/mobile/option/telephone': '音声通話サービス',
  '/mobile/option/telephone/confirm': '音声通話サービス確認',
  '/mobile/option/telephone/complete': '音声通話サービス完了',
  '/mobile/option/i_filter_entry': 'i-フィルター申込',
  '/mobile/option/xperia_premium_quit': 'Xperia限定プレミアム帯域オプション解約',
  '/mobile/option/xperia_premium_quit/complete': 'Xperia限定プレミアム帯域オプション解約完了',
  '/mobile/campaign': '特典・キャンペーン',
  '/mobile/campaign/detail': '特典・キャンペーン詳細',
  '/mobile/campaign/cashback': 'キャッシュバック口座登録',
  '/mobile/campaign/cashback/wcos': 'キャッシュバック口座登録',
  '/mobile/login_history': 'ログイン履歴',
  '/mobile/notification_history': '通知履歴',
  '/mobile/onestop/login': 'ワンストップログイン',
  '/mobile/onestop/error': 'ワンストップエラー',
};
/** NURO 光マイページのサイト名 */
const SITE_NAME_MYPAGE = 'NURO マイページ';
/** NURO モバイルのサイト名 */
const SITE_NAME_MOBILE = 'NURO モバイル';

const Layout = ({ children }: Props) => {
  const router = useRouter();
  const serviceType = useServiceType();
  /** ページ遷移履歴を管理（現在のパス, 直前のパス） */
  const [history, setHistory] = useState([router.asPath, '']);
  /** ヘッダー・フッターの表示制御フラグ（MNPワンストップ等の特殊ページでは非表示にする） */
  const [layoutFlg, setLayoutFlg] = useState(true);

  // モバイルページではAdobe DTM（_satellite）タグを削除する
  // ※SSR時にはwindowが存在しないためtypeof チェックが必要
  if (typeof window === 'object') {
    if (router.asPath.includes('/mobile/')) {
      document.getElementById('satellite')?.remove();
    }
  }

  // ルート変更時の副作用: 履歴更新、レイアウト制御、DTMスクリプト管理
  useEffect(() => {
    // 履歴を1つ分だけ保持（「戻る」ボタンの遷移先判定などに使用）
    setHistory([router.asPath, history[0]]);

    // ページ別のheaderとfooterの非表示
    // ※契約詳細やMNPワンストップはWebView等から表示されるためレイアウトを隠す
    if (router.asPath === PagePathMobile.detail) {
      setLayoutFlg(false);
    }
    if (router.pathname.startsWith(PagePathMobile.mnpOnestopLogin)) {
      setLayoutFlg(false);
    }
    // ログイン画面・ワンストップエラー画面ではレイアウトを再表示
    if (router.asPath === PagePathMobile.login) {
      setLayoutFlg(true);
    }
    if (router.asPath === PagePathMobile.mnpOnestopError) {
      setLayoutFlg(true);
    }

    // 光（非モバイル）ページではAdobe DTM _satellite.pageBottom() を動的に挿入
    // モバイルページでは初回レンダリング時に削除済みのため再挿入しない
    const satellite = document.getElementById('satellite');

    if (!router.asPath.includes('/mobile/')) {
      if (!satellite) {
        const body = document.getElementsByTagName('body')[0] as HTMLElement;
        const scriptUrl = document.createElement('script');
        scriptUrl.id = 'satellite';
        scriptUrl.type = 'text/javascript';
        scriptUrl.innerHTML = 'if(typeof _satellite != "undefined")_satellite.pageBottom();';
        body.appendChild(scriptUrl);
      }
    }
  }, [router.asPath]);

  // ページタイトルを動的に決定
  const isMobile = router.pathname.startsWith('/mobile');
  let pageTitle = titleMap[router.pathname] || '';

  // 10Gアップグレードの場合、選択したプランによってタイトルを出し分ける
  // 申込
  if (router.pathname === '/contract/g10upgrade/applicationplan') {
    const { plan } = router.query;
    if (plan === 'three') {
      pageTitle = 'NURO 光 10ギガプラン（3年契約）申込';
    } else if (plan === 'nothing') {
      pageTitle = 'NURO 光 10ギガプラン（契約期間なし）申込';
    } else {
      pageTitle = '';
    }
  }
  // 申込同意
  if (router.pathname === '/contract/g10upgrade/agreement') {
    const { agree } = router.query;
    if (agree === 'three') {
      pageTitle = 'NURO 光 10ギガプラン（3年契約）申込同意';
    } else if (agree === 'nothing') {
      pageTitle = 'NURO 光 10ギガプラン（契約期間なし）申込同意';
    } else {
      pageTitle = '';
    }
  }

  /** ページタイトルに使用するサイト名（モバイル/光で切り替え） */
  const SITE_NAME = isMobile ? SITE_NAME_MOBILE : SITE_NAME_MYPAGE;

  return (
    <>
      {/* Next.js Head: ページメタ情報とSEO設定 */}
      <Head>
        <meta charSet='UTF-8' />
        <meta
          name='viewport'
          content='width=device-width, initial-scale=1.0, minimum-scale=1.0, user-scalable=no'
        />
        <meta httpEquiv='X-UA-Compatible' content='ie=edge' />
        <title>{pageTitle ? `${pageTitle} - ${SITE_NAME}` : SITE_NAME}</title>
        <meta name='description' content='光回線インターネットサービス NURO 光' />
        <meta
          name='keywords'
          content='NURO 光,ヌロ,ニューロ,高速,世界最速,光ファイバー,ブロードバンド,光回線,インターネット接続,So-net,ソネット,ソニーネットワークコミュニケーションズ,SonyNetworkCommunications'
        />
        <link rel='canonical' href='https://www.nuro.jp/app/mypage/login/' />
        <meta name='format-detection' content='telephone=no' />
        <meta name='theme-color' content='#000000' />
        <link
          rel='apple-touch-icon-precomposed'
          href={url('/images/apple-touch-icon-precomposed.png')}
        />
        <link rel='shortcut icon' href={url('/favicon.ico')} type='image/vnd.microsoft.icon' />
      </Head>
      <GoogleTagManager />
      <div className='headerSimpleHikari__content noSideMenu'>
        {/* レイアウトフラグに応じてヘッダー表示を切り替え */}
        {layoutFlg && <Header />}
        {/* 光サービス利用者のみログインメッセージバナーを表示 */}
        {serviceType === Constants.ServiceType.HIKARI && <LoginMessageBanner />}
        {/* historyContextで遷移履歴を子コンポーネントに提供 */}
        <historyContext.Provider value={history}>{children}</historyContext.Provider>
        {/* グローバルUI: ローディング、アラートダイアログ、チャットボット */}
        <LoadingIcon />
        <AlertDialog />
        <AlertDialogGlobal />
        <ChatBot />
        {layoutFlg && <Footer />}
      </div>
    </>
  );
};

export default Layout;
